import { useEffect, useMemo, useRef, useState } from 'react';
import { bugtracker } from '@99designs/design-services-common';
import {
    IntelligentStyleDesignsQuery,
    useIntelligentStyleDesignsQuery,
} from './IntelligentStyleDesigns.generated';

type Option = {
    isSelected: boolean;
};

export type Design = {
    id: string;
    fullSizeUri: string;
    thumbnailUri: string;
    adminTags: Array<string>;
};

export type DesignOption = {
    id: string;
    fullSizeUri: string;
    thumbnailUri: string;
    adminTags: Array<string>;
} & Option;

export type IntelligentStyleDesign = Omit<
    IntelligentStyleDesignsQuery['expertDesigns']['designs'][0],
    '__typename'
>;

class DesignOptions {
    private designOptions: DesignOption[] = [];
    private designOptionsMap: { [id: string]: DesignOption } = {};

    static MAX_SELECTED = 10;

    /**
     * Sets the available design options while ensuring uniqueness.
     */
    setDesignOptions(newDesignOptions: DesignOption[]) {
        this.designOptionsMap = {};
        const uniqueDesigns = newDesignOptions.filter((design) => {
            if (this.designOptionsMap[design.id]) {
                return false;
            }
            this.designOptionsMap[design.id] = design;
            return true;
        });
        this.designOptions = uniqueDesigns;
    }

    all(): DesignOption[] {
        return this.designOptions;
    }

    totalSelected() {
        return this.selectedDesigns().length;
    }

    selectedDesigns(): Design[] {
        return this.designOptions.reduce<Design[]>((selected, { isSelected, ...design }) => {
            if (isSelected) {
                selected.push(design);
            }
            return selected;
        }, []);
    }
}

const emptyIntelligentStyleDesigns: IntelligentStyleDesign[] = [];

type SelectedState = { [id: string]: boolean };

function designsToState(designs: Design[]) {
    return designs.reduce((result, design) => {
        result[design.id] = true;
        return result;
    }, {} as SelectedState);
}

const CATEGORIES_FLYERS = ['postcard-flyer-design'];

const CATEGORIES_BUSINESS_CARDS = [
    'business-card-design',
    'brand-identity-pack',
    'stationery-design',
];

const lowercaseMpvIdToCategoriesMap: Record<string, string[]> = {
    flyers: CATEGORIES_FLYERS,
    flyersandleaflets: CATEGORIES_FLYERS,
    standardbusinesscards: CATEGORIES_BUSINESS_CARDS,
};

/**
 * useDesignOptions abstracts the logic of external fetching and manages design options and their selected state.
 * Enables IntelligentStylesField to focus on rendering and user interactions.
 */
function useDesignOptions(
    initialSelectedDesigns: Design[],
    printMpvId?: string,
    searchQuery?: string
) {
    const [selected, setSelected] = useState<SelectedState>(designsToState(initialSelectedDesigns));

    /**
     * Track if selected designs have changed
     */
    const changedRef = useRef<boolean>(false);

    // Optimistically return array of the MPV ID if no mapping exists.

    const categories = printMpvId
        ? lowercaseMpvIdToCategoriesMap[printMpvId.toLowerCase()] || [printMpvId]
        : [];

    const { data, loading, error } = useIntelligentStyleDesignsQuery({
        variables: {
            input: {
                searchQuery,
                categories,
                isStyleTagged: true,
                pageSize: 30,
                page: 1,
            },
        },
    });

    const intelligentStylesDesigns: IntelligentStyleDesign[] =
        data?.expertDesigns.designs || emptyIntelligentStyleDesigns;

    const totalIntelligentDesigns = intelligentStylesDesigns.length;

    useEffect(() => {
        if (error) {
            bugtracker.notify(error);
            return;
        }
        if (!loading && totalIntelligentDesigns === 0) {
            bugtracker.notify(new Error('no designs found for IntelligentStylesField'));
        }
    }, [loading, totalIntelligentDesigns, error]);

    const designOptions = useMemo(() => {
        const designOptions = intelligentStylesDesigns.map((design) => {
            const isSelected = !!selected[design.id];
            return {
                id: design.id,
                thumbnailUri: design.designThumbnail,
                fullSizeUri: design.design,
                adminTags: design.adminTags as string[],
                isSelected,
            };
        });
        const designs = new DesignOptions();
        designs.setDesignOptions(designOptions);

        return designs;
    }, [intelligentStylesDesigns, selected]);

    return {
        designOptions,
        loading,
        selected,
        hasChanged: changedRef.current,
        setSelected(newSelected: SelectedState) {
            changedRef.current = true;
            setSelected(newSelected);
        },
        error,
    };
}

export { DesignOptions, useDesignOptions };
